import {FC, Children, cloneElement, isValidElement, ReactElement} from 'react';
import {
    Table as TableImpl,
    TableCellProps,
    ButtonProps,
    Button,
    Icon,
    TableProps as TableImplProps
} from 'semantic-ui-react';
import {SemanticICONS} from 'semantic-ui-react/dist/commonjs/generic';
import {CSSProperties} from 'styled-components';


export interface TableProps extends TableImplProps {
    rows: any[];
    equalCellWidth?: boolean;
    style?: CSSProperties | undefined;
    highlighted?: { isHighlighted: Function, highlightIndex: number, highlightClassName: string };
}


const noop = () => {
};


const Table: FC<TableProps> = props => {
    const {style, equalCellWidth, children, rows, sort, ...tableProps} = props;

    const defaultTableProps: TableImplProps = {
        celled: true,
        sortable: true,
        compact: true,
        margintop: '16px'
    };

    const renderHeader = () => {
        const header = Children
            .toArray(children)
            .filter(isTableCell)
            .map(({props}) => (
                {name: props.header, onClick: props.headerOnClick ?? noop}
            ));

        const sortIcon = (item: any) => {
            if (item.onClick !== noop) {
                let iconName: string = 'sort'
                let itemName = item.name.toLowerCase().replace(' ', '_')
                itemName = itemName === 'result' ? 'res' : itemName
                if (sort !== undefined && sort.field === itemName) {
                    if (sort.operator === 'errors') {
                        iconName = 'exclamation circle'
                    } else if (sort.operator === '>') {
                        iconName = 'sort down'
                    } else {
                        iconName = 'sort up'
                    }
                }
                // @ts-ignore
                return <Icon name={iconName} style={{margin: '0'}} className='floated'/>
            }
        }

        return (
            <TableImpl.Header>
                <TableImpl.Row>
                    {header.map((item, index) => (
                        <TableImpl.HeaderCell
                            key={index}
                            textAlign='center'
                            onClick={item.onClick}
                        >
                            {item.name} {sortIcon(item)}
                        </TableImpl.HeaderCell>
                    ))}
                </TableImpl.Row>
            </TableImpl.Header>
        );
    };

    const renderRows = () => {
        const {isHighlighted = () => false, highlightIndex = -1, highlightClassName = ''} = props.highlighted || {};
        return Children.toArray(rows.map(row => (
            <TableImpl.Row style={{wordWrap: 'break-word'}}>
                {Children.map(children, (cell, columnIndex) =>
                    isValidElement(cell) ? cloneElement(cell as ReactElement, {
                        data: row,
                        className: isHighlighted(row) && columnIndex === highlightIndex ? highlightClassName : '',
                    }) : cell
                )}
            </TableImpl.Row>
        )));
    };

    return (
        <TableImpl {...{...defaultTableProps, ...tableProps}}>
            {renderHeader()}

            <TableImpl.Body>
                {renderRows()}
            </TableImpl.Body>
        </TableImpl>
    );
};


interface AbstractCellProps extends TableCellProps {
    header: string;

    headerOnClick?(): void;

    data?: any;
}

function isTableCell(elem: any): elem is ReactElement<AbstractCellProps> {
    if (!isValidElement(elem)) return false;
    const {props} = elem;
    return typeof props === 'object' && props !== null && 'header' in props;
}


interface CellProps extends AbstractCellProps {
    value(data: any): any;
    render?(value: any): any;
    textStyle?: object;
}


export const Cell: FC<CellProps> = ({render, value, data, ...rest}) => (
    <TableImpl.Cell textAlign='center' style={{fontSize: '14px'}} {...rest}>
        {render ? render(value(data)) : value(data)}
    </TableImpl.Cell>
);

export const TextCell: FC<CellProps> = ({textStyle, ...props}) => (
    <Cell
        {...props}
        render={value => <p style={{display: 'inline', ...textStyle}}>{value ?? 'null'}</p>}
    />
);


interface ButtonCellProps extends CellProps {
    label?(value: any): string;

    onClick?(value: any): void;

    button?: ButtonProps;
    icon?: SemanticICONS;
}

export const ButtonCell: FC<ButtonCellProps> = ({label, onClick, color, button, icon, ...props}) => {
    const buttonProps: ButtonProps = {
        basic: true,
        color: color,
        icon: true,
        size: 'medium'
    };

    if (label) {
        props.fluid = '';
        buttonProps.fluid = true;
        buttonProps.labelPosition = 'left';
    } else {
        props.collapsing = true;
        buttonProps.circular = true;
    }

    const {value, data} = props;

    return (
        <Cell
            {...props}
            onClick={() => onClick && onClick(value(data))}
            render={value => (
                <Button {...{...buttonProps, ...button}}>
                    {label && label(value)}
                    <Icon name={icon}/>
                </Button>
            )}
        />
    );
};


interface ActionsCellProps extends AbstractCellProps {
    actions: { icon: string, onClick(data: any): void }[];
    data?: any;
}

export const ActionsCell: FC<ActionsCellProps> = ({actions, data, ...rest}) => {
    return (
        <Cell
            {...rest}
            value={() => undefined}
            render={() => Children.toArray(actions.map(({icon, onClick}) => (
                <Button circular icon={icon} onClick={() => onClick(data)}/>
            )))}
        />
    );
};


export default Table;
